











































import { Component, Prop, Vue } from 'vue-property-decorator';
import PageHeader from '@/shared/components/pageHeader/PageHeader.vue';
import BotPreviewMessagingClientContainer from '@/features/bots/components/botTestTool/BotPreviewMessagingClientContainer.vue';
import ContactDetails from '@/features/contacts/components/ContactDetails.vue';
import { ContactType } from '@/features/contacts/models/contactType';
import { ContactViewModel } from '@/features/contacts/models/contactViewModel';
import { BotUserViewModel } from '@/features/bots/models/botUserViewModel';
import FlowSelector from '@/features/bots/components/botTestTool/FlowSelector.vue';
import { FlowListItemViewModel } from '../../models/flowListItemViewModel';
import { Action, Getter } from 'vuex-class';
import { LOAD_ANSWERS } from '@/store/answers/actionTypes';
import { LOAD_FLOWS } from '@/store/bot-designer/actionTypes';
import { FlowSelectorItemViewModel } from '../../models/flowSelectorItemViewModel';
import { FlowStatus } from '../../models/flowStatus';
import { BOT_STUDIO_ENABLED } from '@/store/app/featureTypes';
import { AnswerListItemViewModel } from '@/features/bots/models/answers/answerListItemViewModel';
import LoadingSpinner from '@/shared/components/loadingSpinner/LoadingSpinner.vue';
import TimeSelector from '@/features/bots/components/botTestTool/TimeSelector.vue';

@Component({
	components: {
		PageHeader,
		BotPreviewMessagingClientContainer,
		ContactDetails,
		FlowSelector,
		LoadingSpinner,
		TimeSelector
	}
})
export default class BotPreviewModal extends Vue {
	@Prop() public flowToPreview: string;
	@Prop() public answerToPreview: string;

	public botUser: BotUserViewModel = { id: '', data: {} };
	public contact: ContactViewModel = {
		id: '',
		type: ContactType.Lead,
		name: '',
		givenName: '',
		surname: '',
		email: '',
		phoneNumber: '',
		avatarUrl: '',
		customAttributes: {}
	};
	public flows: FlowListItemViewModel[] = [];
	public answers: AnswerListItemViewModel[] = [];

	public flowSelection: FlowSelectorItemViewModel[] = [];
	public answerSelection: FlowSelectorItemViewModel[] = [];
	public currentTime = new Date();
	public loaded: boolean = false;

	@Getter public isWorkspaceFeatureEnabled: (feature: string, defaultValue?: boolean) => boolean;
	@Action(LOAD_FLOWS) public loadFlows: (doLoad: boolean) => Promise<FlowListItemViewModel[]>;
	@Action(LOAD_ANSWERS) public loadAnswers: (doLoad?: boolean) => Promise<AnswerListItemViewModel[]>;

	public async mounted() {
		await this.getFlows();
	}

	public async getFlows() {
		this.flows = await this.loadFlows(false);
		this.answers = await this.loadAnswers(false);
		this.enableCurrentFlowOrAnswer();
		const botStudioEnabled = this.isWorkspaceFeatureEnabled(BOT_STUDIO_ENABLED, true);

		this.flowSelection = this.flows
			.sort((a, b) => b.priority - a.priority)
			.map((f) => ({
				id: f.id,
				name: f.name,
				status: f.status,
				isSelected: !botStudioEnabled ? true : f.status === FlowStatus.Live,
				priority: f.priority
			}));

		this.answerSelection = this.answers
			.sort((a1, a2) => a1.title.localeCompare(a2.title))
			.map((f) => ({
				id: f.id,
				name: f.title,
				status: f.status,
				isSelected: !botStudioEnabled ? true : f.status === FlowStatus.Live,
				priority: 1
			}));
		this.loaded = true;
	}

	public handleBotUserUpdated(updateUser: BotUserViewModel) {
		Object.entries(updateUser.data).forEach(([key, val]) => this.applyBotUserUpdateToContact(key, val));
		this.contact = { ...this.contact }; // hack to make it reactive as nullable dates and dictionary entries not reactive
	}

	public handleContactUpdated() {
		this.updateBotUser('appUser.type', this.contact.type);
		this.updateBotUser('appUser.givenName', this.contact.givenName);
		this.updateBotUser('appUser.surname', this.contact.surname);
		this.updateBotUser('appUser.name', this.getName());
		this.updateBotUser('appUser.email', this.contact.email);
		this.updateBotUser('appUser.phoneNumber', this.contact.phoneNumber);
		this.updateBotUser('appUser.firstSeen', this.contact.firstSeenUtc);
		this.updateBotUser('appUser.lastSeen', this.contact.lastSeenUtc);
		this.updateBotUser('appUser.lastContacted', this.contact.lastContactedUtc);
		this.updateBotUser('appUser.lastHeardFrom', this.contact.lastHeardFromUtc);

		Object.entries(this.contact.customAttributes).forEach(([key, val]) => {
			this.updateBotUser(`appUser.customAttributes.${key}`, val);
		});
	}

	public resetContact() {
		this.contact = {
			id: '',
			type: ContactType.Lead,
			name: '',
			givenName: '',
			surname: '',
			email: '',
			phoneNumber: '',
			avatarUrl: '',
			firstSeenUtc: undefined,
			lastHeardFromUtc: undefined,
			lastSeenUtc: undefined,
			lastContactedUtc: undefined,
			customAttributes: {}
		};
		this.handleContactUpdated();
		this.resetCustomAttributes();
	}

	public isFlowSelectorVisible() {
		return this.flows.length > 0;
	}

	public isAnswerSelectorVisible() {
		return this.answers.length > 0;
	}

	private enableCurrentFlowOrAnswer() {
		if (this.flowToPreview) {
			const flow = this.flows.find(f => f.id === this.flowToPreview);
			if (flow) {
				flow.status = FlowStatus.Live;
			}
		}

		if (this.answerToPreview) {
			const answer = this.answers.find(f => f.id === this.answerToPreview);
			if (answer) {
				answer.status = FlowStatus.Live;
			}
		}
	}

	public get selectedFlows() {
		return this.flows
				.filter(flow => this.flowSelection.find(f => f.id === flow.id)?.isSelected)
				.map(flow => ({
					...flow,
					priority: this.flowSelection.find(f => f.id === flow.id)?.priority
				}));
	}

	public get selectedAnswers() {
		return this.answers.filter(flow => this.answerSelection.find(f => f.id === flow.id)?.isSelected);
	}

	private resetCustomAttributes() {
		Object.entries(this.botUser.data).forEach(([key, val]) => {
			if (key.startsWith('appUser.customAttributes.')) {
				this.updateBotUser(key, undefined);
			}
		});
	}

	private getName() {
		let name = '';
		if (this.contact.givenName) {
			name = this.contact.givenName;
		}
		if (this.contact.surname) {
			name = `${name} ${this.contact.surname}`;
		}
		return name;
	}

	private applyBotUserUpdateToContact(name: string, value: any) {
		if (name.startsWith('appUser.customAttributes')) {
			const attributeName = name.substring(name.lastIndexOf('.') + 1);
			this.contact.customAttributes[attributeName] = value;
		} else if (name === 'appUser.name') {
			this.contact.name = value;
		} else if (name === 'appUser.givenName') {
			this.contact.givenName = value;
		} else if (name === 'appUser.surname') {
			this.contact.surname = value;
		} else if (name === 'appUser.email') {
			this.contact.email = value;
		} else if (name === 'appUser.phoneNumber') {
			this.contact.phoneNumber = value;
		} else if (name === 'appUser.lastSeen') {
			this.contact.lastSeenUtc = value;
		} else if (name === 'appUser.firstSeen') {
			this.contact.firstSeenUtc = value;
		} else if (name === 'appUser.lastContacted') {
			this.contact.lastContactedUtc = value;
		} else if (name === 'appUser.lastHeardFrom') {
			this.contact.lastHeardFromUtc = value;
		}
	}

	private updateBotUser(key: string, value: any) {
		Vue.set(this.botUser.data, key, value);
	}
}
